/**
 * AK-938 — «Заложить опыт», шаг 4: контуры вариантов на карте поля.
 *
 * Чистый слой поверх `buildZonePlan` (тот же, что «Разделить поле на зоны»):
 * каждая нарисованная фигура обрезается контуром поля, фигуры не должны
 * перекрываться. Остаток поля в опыт не входит («серое — остаток поля»), поэтому
 * зону-остаток выбрасываем. Фигура N → вариант N в порядке схемы
 * (К, Э, 1, 2, …): так агроном рисует слева направо, как в low-fi.
 */
import {
  buildZonePlan,
  type EditorShapes,
  type ZoneDraft,
} from '@/components/fields/card/zoneSplit';
import type { GeoLike } from '@/components/fields/card/geometryLogic';
import type { ContourGeo } from '@/components/fields/card/widgets/widgetApi';

export interface VariantZone {
  geojson: ZoneDraft['geojson'];
  areaHa: number;
}

export type VariantZonesPlan =
  | { ok: true; zones: VariantZone[] }
  | { ok: false; error: string };

const CONTOUR_PRIORITY = ['reference', 'working', 'legacy'];

/** Контур поля для обрезки: эталон → рабочий → легаси → любой с геометрией. */
export function pickFieldContour(contours?: ContourGeo[] | null): GeoLike | null {
  const withGeo = (contours ?? []).filter((c) => c.geojson?.coordinates);
  if (!withGeo.length) return null;
  for (const t of CONTOUR_PRIORITY) {
    const hit = withGeo.find((c) => c.contour_type === t);
    if (hit) return hit.geojson as GeoLike;
  }
  return withGeo[0].geojson as GeoLike;
}

/**
 * Зоны вариантов из нарисованных фигур. Фигур больше, чем вариантов, — ошибка
 * словами (лишнюю фигуру отменить), меньше — план частичный: экран покажет,
 * какие варианты ещё не нарисованы.
 */
export function planVariantZones(
  contour: GeoLike | null,
  shapes: EditorShapes,
  variantsCount: number,
  existingZones: Array<GeoLike | null | undefined> = [],
): VariantZonesPlan {
  if (!shapes.length) return { ok: true, zones: [] };
  if (shapes.length > variantsCount) {
    return {
      ok: false,
      error: `Фигур больше, чем вариантов (${shapes.length} из ${variantsCount}) — отмените лишнюю`,
    };
  }
  const plan = buildZonePlan(contour, shapes, { existingZones });
  if (!plan.ok) return plan;
  const drawn = plan.zones
    .filter((z) => !z.isRest && z.shapeIndex != null)
    .sort((a, b) => (a.shapeIndex ?? 0) - (b.shapeIndex ?? 0));
  if (drawn.length < shapes.length) {
    return {
      ok: false,
      error: 'Часть фигур вышла за поле или слишком мала — нарисуйте их внутри контура',
    };
  }
  return { ok: true, zones: drawn.map((z) => ({ geojson: z.geojson, areaHa: z.areaHa })) };
}
